import axios, { AxiosInstance } from 'axios';
import { SDKConfig, GeoData, IPParams, CompaniesSearchParams, CompanyFetchParams, ContactMatchParams, CompanyMatchParams, FormEvents } from './types';
import { IsJunkName } from './utils';
declare const process: {
    env: {
        SERVICE_URL: string;
        EVENT_LOG_URL: string;
    }
}
export class sfSDK {
    public sessionID: string;
    public config: SDKConfig;
    public geoData: GeoData;
    public callIndex: number;
    private selectMeth: string = 'clientSDK';
    private serviceURL = process.env.SERVICE_URL || 'https://dev-smartformsapi.aws.reachforce.com/smartformsapi/';
    private http: AxiosInstance;
    private genUUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
    private isJunkName = IsJunkName;
    private setDefaultConfig(): void {
        this.config = {
            phoneFormat: '###-###-####',
            token: '',
            useSmartInferredGeo: false,
            resultLength: 5,
            remediationLevel: 'review',
            getInferredCompDetails: false,
            showAPIConsoleError: false,
            rfAppVersion: 'SF_SDK_V2.0'
        };
    }
    public makeCall(endpoint: string, params: any): Promise<any> {
        return this.http.post(endpoint, params);
    }
    public matchIP(params?: IPParams): Promise<any> {
        let findByIP: boolean;
        if (typeof params.findByIP !== 'undefined') {
            findByIP = params.findByIP
        } else {
            findByIP = true;
        }
        const formattedParams = {
            "appendByIP": params.getInferredCompDetails || this.config.getInferredCompDetails,
            "ip": params.ip,
            "findByIP": findByIP,
            "appVersion": this.config.rfAppVersion,
            "phoneFormat": params.phoneFormat || this.config.phoneFormat,
            "suid": this.sessionID
        };
        return this.makeCall('ip/match', formattedParams);
    }
    public companiesSearch(params: CompaniesSearchParams): Promise<any> {
        const formattedParams = {
            "junkNameDetected": this.isJunkName(params.companyName || ""),
            "suid": this.sessionID,
            "companyName": params.companyName || "",
            "email": params.email || "",
            "phone": params.phone || "",
            "state": params.state || "",
            "stateCode": params.stateCode || "",
            "country": params.country || "",
            "callIndex": this.callIndex,
            "techFilled": params.techFilled || false,
            "resultLength": params.resultLength || this.config.resultLength,
            "ipAreaCode": params.ipAreaCode || this.geoData.ipAreaCode,
            "ipState": params.ipState || this.geoData.ipState,
            "ipStateCode": params.ipStateCode || this.geoData.ipStateCode,
            "ipCountry": params.ipCountry || this.geoData.ipCountry,
            "rfID": params.rfID || "",
            "formEvents": params.formEvents || {}
        }
        this.callIndex++;
        return this.makeCall('companies/search', formattedParams);
    }
    public companyFetch(params: CompanyFetchParams): Promise<any> {
        const formattedParams = {
            "rfID": params.rfID,
            "phoneFormat": params.phoneFormat || this.config.phoneFormat,
            "inferredURL": params.inferredURL || "",
            "method": params.selectMeth || this.selectMeth,
            "appVersion": this.config.rfAppVersion,
            "suid": this.sessionID,
        }
        return this.makeCall('company/fetch', formattedParams);
    }
    public companyMatch(params: CompanyMatchParams): Promise<any> {
        const formattedParams = {
            "remediationLevel": params.remediationLevel || this.config.remediationLevel,
            "junkNameDetected": this.isJunkName(params.companyName || ""),
            "appVersion": this.config.rfAppVersion,
            "firstName": params.firstName,
            "lastName": params.lastName,
            "email": params.email,
            "suid": this.sessionID,
            "ipCity": params.ipCity || this.geoData.ipCity,
            "ipAreaCode": params.ipAreaCode || this.geoData.ipAreaCode,
            "ipState": params.ipState || this.geoData.ipState,
            "ipStateCode": params.ipStateCode || this.geoData.ipStateCode,
            "ipCountry": params.ipCountry || this.geoData.ipCountry,
            "browserIP": params.ip,
            "inferredURL": params.url,
            "streetAddress1": params.streetAddress1,
            "streetAddress2": params.streetAddress2,
            "city": params.city,
            "state": params.state,
            "zip": params.zip,
            "companyName": params.companyName,
            "country": params.country,
            "phone": params.phone,
            "phoneFormat": params.phoneFormat || this.config.phoneFormat,
            "formEvents": params.formEvents || {}
        };
        return this.makeCall('company/match', formattedParams);
    }
    public contactMatch(params: ContactMatchParams): Promise<any> {
        const formattedParams = {
            "phoneFormat": params.phoneFormat || this.config.phoneFormat,
            "email": params.email,
            "firstName": params.firstName,
            "lastName": params.lastName,
            "title": params.title,
            "company": params.company,
            "companyURL": params.companyURL,
            "country": params.country,
            "state": params.state,
            "city": params.city,
            "suid": this.sessionID,
            "appVersion": this.config.rfAppVersion,
            "formEvents": params.formEvents || {}
        };
        return this.makeCall('contact/match', formattedParams);
    }
    public logEvent(events: FormEvents): Promise<any> {
        let params = {
            formEvents: events,
            suid: this.sessionID,
            appVersion: this.config.rfAppVersion,
        }
        return this.makeCall('event/log', params);
    }
    private async setGeoData() {
        try {
            const res = await this.matchIP();
            this.geoData.ipCountry = res.data.ipInferred.country;
            this.geoData.ipCity = res.data.ipInferred.city;
            this.geoData.ipState = res.data.ipInferred.state;
            this.geoData.ipStateCode = res.data.ipInferred.stateCode;
            this.geoData.ipAreaCode = res.data.ipInferred.areaCode;
        } catch (error) {
            if (this.config.showAPIConsoleError) {
                console.error(error);
            }
        }
    }
    public async logExit() {
        this.logEvent({
            action: 'exit',
            fieldID: '',
            fieldName: '',
            fieldClass: '',
            formID: '',
            dropdown: '',
            formName: '',
            formClass: ''
        })
    }
    public async logLoad() {
        this.logEvent({
            action: 'load',
            fieldID: '',
            fieldName: '',
            fieldClass: '',
            formID: '',
            dropdown: '',
            formName: '',
            formClass: ''
        })
    }
    constructor(config: SDKConfig) {
        this.geoData = {
            ipState: '',
            ipStateCode: '',
            ipCountry: '',
            ipAreaCode: '',
            ipCity: ''
        }
        if (config) {
            this.config = config;
            this.setDefaultConfig();
            Object.keys(config).forEach(key => {
                this.config[key] = config[key];
            })
        }
        this.callIndex = 0;
        this.sessionID = this.genUUID();
        this.http = axios.create({
            baseURL: this.serviceURL,
            timeout: 20000,
            headers: {
                'Authorization': this.config.token,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });
        if (this.config.useSmartInferredGeo) {
            this.setGeoData();
        }
        if (typeof this.config.sdkLoadCallback === 'function') {
            this.config.sdkLoadCallback();
        }
        this.logLoad();
    }
}